<template>
  <div class="settings-page">
    <!-- Page Header -->
    <div class="settings-hero">
      <div class="settings-container settings-hero-inner">
        <div class="settings-hero-copy">
          <div class="settings-hero-icon">⚙</div>
          <div>
            <p class="settings-kicker">Workspace controls</p>
            <h1 class="settings-title">Settings</h1>
            <p class="settings-subtitle">Manage account access, notification rules, usage pricing, and guardrails.</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Content -->
    <div class="settings-container settings-content">
      <div class="settings-overview-grid">
        <div class="settings-overview-card">
          <span class="settings-overview-label">Signed in as</span>
          <strong>{{ user?.email || '—' }}</strong>
        </div>
        <div class="settings-overview-card">
          <span class="settings-overview-label">Role</span>
          <strong>{{ isAdmin ? 'Tenant admin' : 'Member' }}</strong>
        </div>
        <div class="settings-overview-card">
          <span class="settings-overview-label">Credit limit</span>
          <strong>{{ creditBalance?.limit != null ? creditBalance.limit.toFixed(2) : 'No limit' }}</strong>
        </div>
      </div>

      <div class="settings-stack">

        <!-- Account Section -->
        <div class="settings-card settings-account-card">
          <div class="settings-section-head">
            <div>
              <h2 class="settings-card-title">Account information</h2>
              <p class="settings-muted">Your current workspace identity.</p>
            </div>
          </div>
          <div class="settings-account-grid">
            <div class="settings-info-tile">
              <label class="settings-label">Email</label>
              <p class="settings-value">{{ user?.email || '—' }}</p>
            </div>
          </div>
        </div>

        <!-- Team (admin only) — moved here from the top nav -->
        <div v-if="isAdmin" class="settings-card" data-testid="settings-team-card">
          <div class="settings-section-head">
            <div>
              <h2 class="settings-card-title">Team</h2>
              <p class="settings-muted">Manage workspace members, roles, and invitations.</p>
            </div>
            <router-link to="/team" class="settings-team-link" data-testid="settings-team-link">
              Manage team
              <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" /></svg>
            </router-link>
          </div>
        </div>

        <!-- Notification Preferences -->
        <div
          class="settings-card"
          data-testid="notification-preferences-card"
        >
          <div class="settings-section-head">
            <div>
              <h2 class="settings-card-title">Notification preferences</h2>
              <p class="settings-muted">
                Choose which optional emails this workspace receives.
                Invitations and password resets are always sent.
              </p>
            </div>
          </div>

          <div
            v-if="prefsLoading && !categories.length"
            class="settings-muted py-4"
            data-testid="notification-prefs-loading"
          >Loading preferences…</div>

          <div
            v-else-if="prefsError"
            class="settings-alert settings-alert-danger"
            role="alert"
            data-testid="notification-prefs-error"
          >{{ prefsError }}</div>

          <div v-else class="space-y-4" data-testid="notification-prefs-list">
            <div
              v-for="category in categories"
              :key="category.key"
              class="settings-preference-row"
              :data-testid="`notification-pref-${category.key}`"
            >
              <div class="pr-4">
                <label
                  :for="`notif-${category.key}`"
                  class="settings-value"
                >{{ category.label }}</label>
                <p class="settings-muted">{{ category.description }}</p>
              </div>
              <input
                :id="`notif-${category.key}`"
                type="checkbox"
                class="settings-checkbox"
                :checked="!!preferences[category.key]"
                :disabled="!isAdmin || savingKey === category.key"
                @change="onToggle(category, $event.target.checked)"
                :data-testid="`notification-pref-toggle-${category.key}`"
              />
            </div>

            <p
              v-if="!isAdmin"
              class="settings-note"
              data-testid="notification-prefs-readonly-note"
            >
              Only tenant admins can change these settings.
            </p>

            <p
              v-if="prefsSuccess"
              class="settings-success"
              data-testid="notification-prefs-success"
            >{{ prefsSuccess }}</p>
          </div>

          <p
            class="settings-note settings-note-spaced"
            data-testid="notification-prefs-transactional-note"
          >
            Transactional emails ({{ transactionalCategoryNames }}) are always
            delivered and ignore the toggles above.
          </p>
        </div>

        <!-- Pricing & Credits -->
        <div class="settings-card" data-testid="pricing-credits-card">
          <div class="settings-section-head">
            <div>
              <h2 class="settings-card-title">Pricing &amp; Credits</h2>
              <p class="settings-muted">Configure tiered usage pricing and credit limits for this workspace.</p>
            </div>
            <span v-if="!isAdmin" class="settings-readonly">Read-only</span>
          </div>

          <!-- Balance summary -->
          <div v-if="creditBalance" class="settings-stat-grid">
            <div class="settings-stat">
              <p class="settings-stat-label">Consumed</p>
              <p class="settings-stat-value">{{ creditBalance.consumed?.toFixed(2) }}</p>
            </div>
            <div class="settings-stat">
              <p class="settings-stat-label">Limit</p>
              <p class="settings-stat-value">{{ creditBalance.limit != null ? creditBalance.limit.toFixed(2) : '∞' }}</p>
            </div>
            <div class="settings-stat">
              <p class="settings-stat-label">Remaining</p>
              <p class="settings-stat-value" :class="creditBalance.available === 0 ? 'settings-danger-text' : 'settings-success-text'">
                {{ creditBalance.available != null ? creditBalance.available.toFixed(2) : '∞' }}
              </p>
            </div>
          </div>
          <p v-else-if="creditBalanceError" class="settings-danger-text settings-paragraph">{{ creditBalanceError }}</p>

          <!-- No policy notice -->
          <div v-if="!pricingPolicyLoading && !pricingPolicy && !isAdmin" class="settings-muted settings-paragraph">
            No active pricing policy configured.
          </div>

          <!-- Tier editor -->
          <div v-if="isAdmin || pricingPolicy" class="space-y-3" data-testid="tier-editor">
            <div class="settings-row-head">
              <p class="settings-label">Usage tiers</p>
              <button
                v-if="isAdmin"
                type="button"
                class="settings-text-btn"
                @click="addTier"
                data-testid="add-tier-btn"
              >+ Add tier</button>
            </div>

            <!-- Header row -->
            <div class="settings-tier-header">
              <span>From (units)</span>
              <span>To (units)</span>
              <span>Rate (credits/unit)</span>
              <span></span>
            </div>

            <div
              v-for="(tier, i) in editTiers"
              :key="i"
              class="settings-tier-row"
              :data-testid="`tier-row-${i}`"
            >
              <div>
                <input
                  type="number" min="0" step="any"
                  class="settings-input"
                  :class="tierErrors[i]?.from ? 'settings-input-error' : ''"
                  :value="tier.from"
                  :disabled="!isAdmin"
                  @input="tier.from = parseFloat($event.target.value)"
                  :data-testid="`tier-from-${i}`"
                />
                <p v-if="tierErrors[i]?.from" class="settings-field-error">{{ tierErrors[i].from }}</p>
              </div>
              <div>
                <input
                  type="number" min="0" step="any"
                  placeholder="∞"
                  class="settings-input"
                  :class="tierErrors[i]?.to ? 'settings-input-error' : ''"
                  :value="tier.to === null ? '' : tier.to"
                  :disabled="!isAdmin"
                  @input="tier.to = $event.target.value === '' ? null : parseFloat($event.target.value)"
                  :data-testid="`tier-to-${i}`"
                />
                <p v-if="tierErrors[i]?.to" class="settings-field-error">{{ tierErrors[i].to }}</p>
              </div>
              <div>
                <input
                  type="number" min="0" step="any"
                  class="settings-input"
                  :class="tierErrors[i]?.rate ? 'settings-input-error' : ''"
                  :value="tier.rate"
                  :disabled="!isAdmin"
                  @input="tier.rate = parseFloat($event.target.value)"
                  :data-testid="`tier-rate-${i}`"
                />
                <p v-if="tierErrors[i]?.rate" class="settings-field-error">{{ tierErrors[i].rate }}</p>
              </div>
              <div class="flex items-center pt-1">
                <button
                  v-if="isAdmin && editTiers.length > 1"
                  type="button"
                  class="settings-remove-btn"
                  @click="removeTier(i)"
                  :data-testid="`tier-remove-${i}`"
                >✕</button>
              </div>
            </div>

            <!-- Credit limit -->
            <div class="settings-limit-row">
              <label class="settings-label whitespace-nowrap">Credit limit</label>
              <input
                type="number" min="0" step="any" placeholder="No limit"
                class="settings-input settings-short-input"
                :disabled="!isAdmin"
                v-model="editCreditLimit"
                data-testid="credit-limit-input"
              />
            </div>

            <!-- Validation errors from server -->
            <div v-if="pricingPolicyError" class="settings-alert settings-alert-danger" data-testid="pricing-error">
              {{ pricingPolicyError }}
            </div>

            <!-- Forecast preview -->
            <div class="settings-forecast" data-testid="forecast-panel">
              <p class="settings-label settings-label-spaced">Preview estimate</p>
              <div class="settings-inline-controls">
                <input
                  type="number" min="0" step="any" placeholder="Usage amount"
                  class="settings-input settings-short-input"
                  v-model.number="previewUsage"
                  data-testid="preview-usage-input"
                />
                <button
                  type="button"
                  class="settings-text-btn"
                  @click="runPreview"
                  :disabled="previewLoading"
                  data-testid="preview-btn"
                >{{ previewLoading ? 'Calculating…' : 'Calculate' }}</button>
              </div>
              <div v-if="previewResult" class="settings-preview-result" data-testid="preview-result">
                <p>Credits required: <strong>{{ previewResult.projectedCredits?.toFixed(4) }}</strong></p>
                <p>Remaining after: <strong :class="previewResult.allowed ? 'settings-success-text' : 'settings-danger-text'">
                  {{ previewResult.remainingAfter != null ? previewResult.remainingAfter.toFixed(4) : '∞' }}
                </strong></p>
                <p class="font-medium" :class="previewResult.allowed ? 'settings-success-text' : 'settings-danger-text'">
                  {{ previewResult.allowed ? 'Within limit' : 'Would exceed limit' }}
                </p>
              </div>
              <p v-if="previewError" class="settings-field-error">{{ previewError }}</p>
            </div>

            <!-- Save -->
            <div v-if="isAdmin" class="settings-save-row">
              <button
                type="button"
                class="settings-btn settings-btn-accent"
                :disabled="pricingPolicySaving || hasTierErrors"
                @click="savePricing"
                data-testid="save-pricing-btn"
              >{{ pricingPolicySaving ? 'Saving…' : 'Save pricing policy' }}</button>
              <p v-if="pricingSaved" class="settings-success" data-testid="pricing-saved-msg">Policy saved</p>
            </div>
          </div>
        </div>

        <!-- Feature1 connection (capability growth integration). Validate-
             before-save: the backend persists credentials only after a live
             capability-export call succeeds, so a bad paste can never break
             an existing working connection. -->
        <div class="settings-card" data-testid="feature1-connect-card">
          <div class="settings-section-head">
            <div>
              <h2 class="settings-card-title">Feature1 connection</h2>
              <p class="settings-muted">
                Connect this workspace to your Feature1 platform to sync capabilities into the
                Capability Map. Generate a long-lived integration token in Feature1 under
                Settings → Integrations → Integration tokens, then paste it here.
              </p>
            </div>
            <span v-if="!isAdmin" class="settings-readonly">Read-only</span>
          </div>

          <div v-if="f1Loading" class="settings-muted">Checking connection…</div>
          <template v-else>
            <div v-if="f1Connection?.connected" class="settings-limit-row" data-testid="feature1-connected">
              <p class="settings-value">
                Connected to {{ f1Connection.base_url }}
                <span class="settings-muted"> · token …{{ f1Connection.token_fingerprint }}</span>
              </p>
              <button
                v-if="isAdmin"
                type="button"
                class="settings-text-btn"
                @click="f1Disconnect"
                data-testid="feature1-disconnect-btn"
              >Disconnect</button>
            </div>
            <p v-else class="settings-muted" data-testid="feature1-not-connected">Not connected.</p>

            <form v-if="isAdmin" class="space-y-3" @submit.prevent="f1Connect" data-testid="feature1-connect-form">
              <div>
                <label class="settings-label">Feature1 API base URL</label>
                <input
                  v-model.trim="f1BaseUrl"
                  type="url"
                  class="settings-input"
                  placeholder="https://your-tenant.feature1.ai/api"
                  data-testid="feature1-base-url-input"
                />
              </div>
              <div>
                <label class="settings-label">Integration token</label>
                <input
                  v-model.trim="f1Token"
                  type="password"
                  class="settings-input"
                  placeholder="Paste the token generated in Feature1"
                  data-testid="feature1-token-input"
                />
              </div>
              <div class="settings-save-row">
                <button
                  type="submit"
                  class="settings-btn settings-btn-accent"
                  :disabled="!f1Token || !f1BaseUrl || f1Saving"
                  data-testid="feature1-connect-btn"
                >{{ f1Saving ? 'Validating…' : f1Connection?.connected ? 'Reconnect' : 'Connect' }}</button>
                <p v-if="f1Success" class="settings-success" data-testid="feature1-connect-success">{{ f1Success }}</p>
              </div>
              <p v-if="f1Error" class="settings-field-error" role="alert" data-testid="feature1-connect-error">{{ f1Error }}</p>
            </form>
          </template>
        </div>

      </div>
    </div>
  </div>
</template>

<script>
import { ref, reactive, computed, watch, onMounted } from 'vue'
import { storeToRefs } from 'pinia'
import { useSitesStore } from '../stores/sites'
import api from '../utils/api'

export default {
  name: 'Settings',
  setup() {
    const store = useSitesStore()

    const prefsLoading = ref(false)
    const prefsError = ref('')
    const prefsSuccess = ref('')
    const savingKey = ref(null)

    const user = computed(() => store.user)
    const isAdmin = computed(() => Boolean(store.user?.isAdmin || store.user?.is_admin))
    const preferences = computed(() => store.notificationPreferences || {})
    const categories = computed(() => store.notificationCategories || [])
    const transactionalCategoryNames = computed(() => {
      const list = store.transactionalCategories || []
      const map = { invitation: 'invitations', password_reset: 'password resets' }
      return list.map((k) => map[k] || k).join(' and ') || 'invitations and password resets'
    })

    const load = async () => {
      prefsLoading.value = true
      prefsError.value = ''
      try {
        await store.fetchNotificationPreferences()
      } catch (err) {
        prefsError.value = err?.response?.data?.error || err?.message
          || 'Failed to load notification preferences'
      } finally {
        prefsLoading.value = false
      }
    }

    const onToggle = async (category, nextValue) => {
      if (!isAdmin.value) return
      prefsError.value = ''
      prefsSuccess.value = ''
      savingKey.value = category.key
      const previous = preferences.value[category.key]
      store.notificationPreferences = { ...preferences.value, [category.key]: nextValue }
      try {
        await store.updateNotificationPreferences({ [category.key]: nextValue })
        prefsSuccess.value = `Saved “${category.label}”`
      } catch (err) {
        prefsError.value = err?.response?.data?.error || err?.message
          || 'Could not save notification preference'
        store.notificationPreferences = { ...preferences.value, [category.key]: previous }
        try { await store.fetchNotificationPreferences() } catch (_) { /* noop */ }
      } finally {
        savingKey.value = null
      }
    }

    // ── Pricing & Credits ────────────────────────────────────────────────────
    const {
      pricingPolicy, pricingPolicyLoading, pricingPolicySaving, pricingPolicyError,
      creditBalance, creditBalanceLoading, creditBalanceError,
    } = storeToRefs(store)

    // Local editable copy of tiers
    const editTiers = ref([{ from: 0, to: null, rate: 1 }])
    const editCreditLimit = ref(null)
    const pricingSaved = ref(false)
    const tierErrors = ref([])

    // Sync editTiers from loaded policy
    watch(pricingPolicy, (p) => {
      if (p) {
        editTiers.value = p.tiers.map(t => ({ ...t }))
        editCreditLimit.value = p.credit_limit ?? null
      }
    }, { immediate: true })

    const validateTiersLocally = () => {
      const errs = editTiers.value.map(() => ({}))
      let hasError = false
      editTiers.value.forEach((t, i) => {
        if (typeof t.from !== 'number' || isNaN(t.from) || t.from < 0) {
          errs[i].from = 'Must be ≥ 0'; hasError = true
        }
        if (typeof t.rate !== 'number' || isNaN(t.rate) || t.rate < 0) {
          errs[i].rate = 'Must be ≥ 0'; hasError = true
        }
        const isLast = i === editTiers.value.length - 1
        if (t.to !== null && t.to !== undefined) {
          if (typeof t.to !== 'number' || isNaN(t.to) || t.to <= t.from) {
            errs[i].to = `Must be > ${t.from}`; hasError = true
          }
        } else if (!isLast) {
          errs[i].to = 'Only last tier can be unbounded'; hasError = true
        }
        if (i > 0 && !errs[i - 1].to && !errs[i].from) {
          const prev = editTiers.value[i - 1]
          if (prev.to !== t.from) {
            errs[i].from = `Must equal previous tier's to (${prev.to})`; hasError = true
          }
        }
      })
      tierErrors.value = errs
      return !hasError
    }

    const hasTierErrors = computed(() => tierErrors.value.some(e => Object.keys(e).length > 0))

    const addTier = () => {
      const last = editTiers.value[editTiers.value.length - 1]
      const newFrom = last.to ?? (last.from + 100)
      // Close last tier before adding new one
      last.to = newFrom
      editTiers.value.push({ from: newFrom, to: null, rate: 1 })
      tierErrors.value = editTiers.value.map(() => ({}))
    }

    const removeTier = (i) => {
      if (editTiers.value.length === 1) return
      editTiers.value.splice(i, 1)
      // Ensure last tier is unbounded
      editTiers.value[editTiers.value.length - 1].to = null
      tierErrors.value = editTiers.value.map(() => ({}))
    }

    const savePricing = async () => {
      pricingSaved.value = false
      store.pricingPolicyError = null
      if (!validateTiersLocally()) return
      try {
        const limit = editCreditLimit.value !== '' && editCreditLimit.value != null
          ? parseFloat(editCreditLimit.value) : null
        await store.savePricingPolicy(editTiers.value, limit)
        pricingSaved.value = true
        await store.fetchCreditBalance()
        setTimeout(() => { pricingSaved.value = false }, 3000)
      } catch (_) { /* error shown via pricingPolicyError ref */ }
    }

    // Preview / forecast
    const previewUsage = ref(null)
    const previewResult = ref(null)
    const previewLoading = ref(false)
    const previewError = ref(null)

    const runPreview = async () => {
      if (previewUsage.value == null || previewUsage.value < 0) return
      previewLoading.value = true
      previewError.value = null
      previewResult.value = null
      try {
        // If policy not saved yet, calculate locally from editTiers
        if (!pricingPolicy.value) {
          previewError.value = 'Save a pricing policy first to preview live data'
          return
        }
        const data = await store.forecastCredits(previewUsage.value)
        previewResult.value = data
      } catch (err) {
        previewError.value = err?.response?.data?.error || 'Forecast failed'
      } finally {
        previewLoading.value = false
      }
    }

    const loadCredits = async () => {
      await Promise.all([store.fetchPricingPolicy(), store.fetchCreditBalance()])
    }

    onMounted(load)
    onMounted(loadCredits)

    // ── Feature1 connection (capability growth integration) ──
    const f1Connection = ref(null)
    const f1Loading = ref(false)
    const f1BaseUrl = ref('')
    const f1Token = ref('')
    const f1Saving = ref(false)
    const f1Error = ref('')
    const f1Success = ref('')

    const f1Load = async () => {
      if (!isAdmin.value) return
      f1Loading.value = true
      try {
        const { data } = await api.get('/capabilities/connection')
        f1Connection.value = data.connection || null
        if (f1Connection.value?.base_url && !f1BaseUrl.value) f1BaseUrl.value = f1Connection.value.base_url
      } catch {
        f1Connection.value = null
      } finally {
        f1Loading.value = false
      }
    }

    const f1Connect = async () => {
      if (f1Saving.value) return
      f1Saving.value = true
      f1Error.value = ''
      f1Success.value = ''
      try {
        const { data } = await api.post('/capabilities/connection', {
          token: f1Token.value,
          base_url: f1BaseUrl.value,
        })
        f1Connection.value = data.connection
        f1Token.value = ''
        f1Success.value = 'Connected — capability sync is ready.'
      } catch (err) {
        f1Error.value = err.response?.data?.error || 'Could not validate the connection.'
      } finally {
        f1Saving.value = false
      }
    }

    const f1Disconnect = async () => {
      f1Error.value = ''
      f1Success.value = ''
      try {
        const { data } = await api.delete('/capabilities/connection')
        f1Connection.value = data.connection
      } catch (err) {
        f1Error.value = err.response?.data?.error || 'Could not disconnect.'
      }
    }

    onMounted(f1Load)

    return {
      // Feature1 connection
      f1Connection,
      f1Loading,
      f1BaseUrl,
      f1Token,
      f1Saving,
      f1Error,
      f1Success,
      f1Connect,
      f1Disconnect,
      user,
      isAdmin,
      preferences,
      categories,
      transactionalCategoryNames,
      prefsLoading,
      prefsError,
      prefsSuccess,
      savingKey,
      onToggle,
      // Credits
      pricingPolicy,
      pricingPolicyLoading,
      pricingPolicySaving,
      pricingPolicyError,
      creditBalance,
      creditBalanceLoading,
      creditBalanceError,
      editTiers,
      editCreditLimit,
      tierErrors,
      hasTierErrors,
      pricingSaved,
      addTier,
      removeTier,
      savePricing,
      previewUsage,
      previewResult,
      previewLoading,
      previewError,
      runPreview,
    }
  }
}
</script>

<style scoped>
.settings-page {
  min-height: 100vh;
  color: var(--text-primary);
  background:
    radial-gradient(circle at 9% -8%, rgba(124,109,250,0.24), transparent 34rem),
    radial-gradient(circle at 86% 4%, rgba(52,211,153,0.10), transparent 30rem),
    linear-gradient(135deg, rgba(255,255,255,0.025), transparent 34rem),
    var(--surface-base);
}

.settings-container {
  width: min(1120px, calc(100% - 32px));
  margin: 0 auto;
}

.settings-hero {
  border-bottom: var(--glass-border);
  background:
    linear-gradient(135deg, rgba(124,109,250,0.12), transparent 42%),
    rgba(15,15,24,0.72);
  backdrop-filter: var(--glass-blur);
  -webkit-backdrop-filter: var(--glass-blur);
}

.settings-hero-inner {
  padding: 38px 0 34px;
}

.settings-hero-copy {
  display: flex;
  align-items: center;
  gap: 18px;
}

.settings-hero-icon {
  width: 56px;
  height: 56px;
  display: grid;
  place-items: center;
  flex-shrink: 0;
  border-radius: 18px;
  color: #fff;
  background: linear-gradient(135deg, var(--accent), #34d399);
  border: 1px solid rgba(255,255,255,0.18);
  box-shadow: 0 0 34px rgba(124,109,250,0.32);
  font-size: 22px;
}

.settings-kicker {
  margin: 0 0 8px;
  color: var(--accent);
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.16em;
  text-transform: uppercase;
}

.settings-title {
  margin: 0;
  color: var(--text-primary);
  font-size: clamp(34px, 5vw, 56px);
  font-weight: 850;
  letter-spacing: -0.06em;
  line-height: 0.96;
}

.settings-subtitle {
  max-width: 640px;
  margin: 12px 0 0;
  color: var(--text-secondary);
  font-size: 15.5px;
  line-height: 1.65;
}

.settings-content {
  padding: 28px 0 56px;
}

.settings-overview-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 14px;
  margin-bottom: 22px;
}

.settings-overview-card {
  padding: 16px 18px;
  border: var(--glass-border);
  border-radius: 18px;
  background: var(--glass-bg);
  box-shadow: var(--elev-sm);
  backdrop-filter: var(--glass-blur);
  -webkit-backdrop-filter: var(--glass-blur);
}

.settings-overview-label {
  display: block;
  margin-bottom: 7px;
  color: var(--text-muted);
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.1em;
  text-transform: uppercase;
}

.settings-overview-card strong {
  display: block;
  color: var(--text-primary);
  font-size: 15px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.settings-stack {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 20px;
}

.settings-card {
  position: relative;
  overflow: hidden;
  padding: 24px;
  border: var(--glass-border);
  border-radius: 22px;
  background:
    linear-gradient(145deg, rgba(255,255,255,0.075), rgba(255,255,255,0.035)),
    var(--glass-bg);
  box-shadow: var(--elev-sm), inset 0 1px 0 rgba(255,255,255,0.06);
  backdrop-filter: var(--glass-blur);
  -webkit-backdrop-filter: var(--glass-blur);
  transition: border-color 0.18s, background 0.18s, transform 0.18s, box-shadow 0.18s;
}

.settings-card::before {
  content: '';
  position: absolute;
  inset: 0;
  pointer-events: none;
  background: radial-gradient(circle at 0% 0%, rgba(255,255,255,0.09), transparent 34%);
  opacity: 0.75;
}

.settings-card > * {
  position: relative;
  z-index: 1;
}

.settings-card:hover {
  border: var(--glass-border-hi);
  background:
    linear-gradient(145deg, rgba(255,255,255,0.095), rgba(255,255,255,0.04)),
    var(--glass-bg-hover);
  box-shadow: var(--elev-md);
  transform: translateY(-1px);
}


.settings-card-title {
  margin: 0 0 16px;
  color: var(--text-primary);
  font-size: 19px;
  font-weight: 800;
  letter-spacing: -0.035em;
}

.settings-team-link {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
  padding: 8px 14px;
  border-radius: var(--radius-sm, 8px);
  background: var(--glass-bg);
  border: var(--glass-border);
  color: var(--text-primary);
  font-size: 13px;
  font-weight: 600;
  text-decoration: none;
  transition: background 0.15s, border-color 0.15s;
}
.settings-team-link:hover { background: var(--glass-bg-hover); border: var(--glass-border-hi); }

.settings-section-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 18px;
}

.settings-label {
  display: block;
  margin: 0 0 7px;
  color: var(--text-muted);
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.settings-label-spaced {
  margin-bottom: 10px;
}

.settings-value {
  margin: 0;
  color: var(--text-primary);
  font-size: 14px;
  font-weight: 700;
}

.settings-muted {
  margin: 0;
  color: var(--text-secondary);
  font-size: 14px;
  line-height: 1.6;
}

.settings-note {
  margin: 0;
  color: var(--text-muted);
  font-size: 12px;
  line-height: 1.6;
}

.settings-note-spaced {
  margin-top: 22px;
}

.settings-paragraph {
  margin: 0 0 16px;
}

.settings-link,
.settings-text-btn {
  color: var(--accent);
  font-size: 13px;
  font-weight: 750;
  text-decoration: none;
}

.settings-link:hover,
.settings-text-btn:hover {
  color: var(--text-primary);
}

.settings-preference-row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  padding: 14px;
  border: var(--glass-border);
  border-radius: 14px;
  background: rgba(255,255,255,0.035);
  transition: background 0.15s, border-color 0.15s;
}

.settings-preference-row:first-child {
  border-top: var(--glass-border);
}

.settings-preference-row:hover {
  background: var(--glass-bg-hover);
  border: var(--glass-border-hi);
}

.settings-checkbox {
  width: 20px;
  height: 20px;
  margin-top: 3px;
  accent-color: var(--accent);
}

.settings-account-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;
}

.settings-info-tile {
  padding: 16px;
  border: var(--glass-border);
  border-radius: 16px;
  background: var(--glass-bg-active);
}

.settings-readonly {
  flex-shrink: 0;
  padding: 5px 9px;
  color: var(--text-muted);
  border: var(--glass-border);
  border-radius: 999px;
  background: var(--glass-bg-active);
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.settings-stat-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
  margin-bottom: 20px;
}

.settings-stat {
  padding: 14px;
  text-align: center;
  border: var(--glass-border);
  border-radius: 16px;
  background: var(--glass-bg-active);
}

.settings-stat-label {
  margin: 0 0 6px;
  color: var(--text-muted);
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.settings-stat-value {
  margin: 0;
  color: var(--text-primary);
  font-size: 16px;
  font-weight: 800;
}

.settings-row-head,
.settings-limit-row,
.settings-inline-controls,
.settings-save-row {
  display: flex;
  align-items: center;
  gap: 12px;
}

.settings-row-head {
  justify-content: space-between;
  margin-bottom: 10px;
}

.settings-tier-header,
.settings-tier-row {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 8px;
}

.settings-tier-header {
  padding: 0 4px;
  color: var(--text-muted);
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.08em;
}

.settings-tier-row {
  align-items: flex-start;
}

.settings-input {
  width: 100%;
  padding: 10px 12px;
  color: var(--text-primary);
  border: var(--glass-border);
  border-radius: 12px;
  background: var(--glass-bg-active);
  font-size: 13px;
  outline: none;
  transition: border-color 0.15s, background 0.15s, box-shadow 0.15s;
}

.settings-input:focus {
  border: var(--glass-border-hi);
  background: var(--glass-bg-hover);
  box-shadow: 0 0 0 3px rgba(124,109,250,0.14);
}

.settings-input:disabled {
  cursor: not-allowed;
  opacity: 0.55;
}

.settings-input-error {
  border-color: rgba(239,68,68,0.7);
}

.settings-short-input {
  max-width: 160px;
}

.settings-field-error,
.settings-danger-text {
  color: #fca5a5;
}

.settings-field-error {
  margin: 4px 0 0;
  font-size: 12px;
}

.settings-success,
.settings-success-text {
  color: #86efac;
}

.settings-success {
  margin: 0;
  font-size: 13px;
}

.settings-alert {
  padding: 10px 12px;
  border-radius: 12px;
  font-size: 13px;
}

.settings-alert-danger {
  color: #fecaca;
  border: 1px solid rgba(239,68,68,0.28);
  background: rgba(239,68,68,0.12);
}

.settings-remove-btn {
  padding: 2px 8px;
  color: #fca5a5;
  border: var(--glass-border);
  border-radius: 999px;
  background: var(--glass-bg);
  font-size: 14px;
}

.settings-remove-btn:hover {
  border-color: rgba(239,68,68,0.38);
  background: rgba(239,68,68,0.12);
}

.settings-limit-row {
  margin-top: 18px;
}

.settings-forecast {
  margin-top: 18px;
  padding: 16px;
  border: var(--glass-border);
  border-radius: 18px;
  background:
    linear-gradient(135deg, rgba(124,109,250,0.10), transparent),
    var(--glass-bg-active);
}

.settings-preview-result {
  margin-top: 14px;
  color: var(--text-secondary);
  font-size: 14px;
  line-height: 1.6;
}

.settings-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 38px;
  padding: 9px 15px;
  border-radius: 999px;
  font-size: 13px;
  font-weight: 800;
  font-family: inherit;
  transition: transform 0.15s, box-shadow 0.15s, border-color 0.15s, background 0.15s;
}

.settings-btn:disabled {
  cursor: not-allowed;
  opacity: 0.55;
}

.settings-btn-accent {
  color: #fff;
  border: 1px solid transparent;
  background: var(--accent);
  box-shadow: 0 0 18px var(--accent-glow);
}

.settings-btn-accent:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 0 26px var(--accent-glow);
}


@media (max-width: 720px) {
  .settings-hero-copy {
    align-items: flex-start;
    flex-direction: column;
  }

  .settings-overview-grid,
  .settings-account-grid {
    grid-template-columns: 1fr;
  }

  .settings-section-head,
  .settings-row-head,
  .settings-limit-row,
  .settings-inline-controls,
  .settings-save-row {
    align-items: flex-start;
    flex-direction: column;
  }

  .settings-stat-grid {
    grid-template-columns: 1fr;
  }

  .settings-tier-header {
    display: none;
  }

  .settings-tier-row {
    grid-template-columns: 1fr;
  }
}
</style>
